import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ContentRefreshDomain } from '@pepper/utils/src/types/domain'
import { disconnectGoogleAccount } from '@pepper/utils/src/api/integrations/google'
import { Button, Modal, PopOver, Spin, useShowMessage } from '@pepper/ui'

import { ReactComponent as GoogleLogo } from 'assets/images/google_logo.svg'
import DisconnectConfirmation from 'components/DisconnectConfirmation'
import { useGaGscProperties } from 'utils/hooks/useGaGscProperties'
import { updateDomain, UpdateDomainPayload } from 'utils/api/domain'
import Domain from './Domain'

import s from './PropertySelectorModal.module.css'

type Props = {
  domains: ContentRefreshDomain[]
  connectedAccount: IIntegrationAccount
  companyId: string
  onSuccess: () => void
  onDisconnect?: () => void
}

export const PropertySelectorModal = ({
  domains,
  connectedAccount,
  companyId,
  onDisconnect,
  onSuccess,
}: Props) => {
  const { url: email, id: googleAccountId } = connectedAccount
  const queryClient = useQueryClient()
  const [updatedValues, setUpdatedValues] = useState<
    Record<string, UpdateDomainPayload>
  >({})
  const showMessage = useShowMessage()
  const [showDisconnectConfirmation, setShowDisconnectConfirmation] =
    useState(false)
  const { gaOptions, gscOptions, gaError, gscError, isLoading } =
    useGaGscProperties(companyId)
  const [isVisible, setIsVisible] = useState(false)

  const handleChange = (
    domainId: string,
    type: 'gsc' | 'ga',
    value?: string
  ) => {
    const _domain = updatedValues[domainId]

    setUpdatedValues({
      ...updatedValues,
      [domainId]: {
        ...(_domain ?? { companyId, domainId }),
        [type]: value,
      },
    })
  }

  const handleSave = async () => {
    const responses = await Promise.allSettled(
      Object.values(updatedValues).map(async payload => {
        const response = await updateDomain(payload)
        return response
      })
    )
    if (responses.some(r => r.status !== 'fulfilled')) {
      showMessage({
        type: 'warning',
        message: 'One or more properties failed to update',
      })
    } else {
      showMessage({ type: 'success', message: 'Properties have been updated' })
    }
    handleClose()
    onSuccess()
  }

  const handleToggle = () => {
    setShowDisconnectConfirmation(prevVal => !prevVal)
  }

  const handleDisconnect = async () => {
    const response = await disconnectGoogleAccount({
      accountId: googleAccountId,
      companyId,
    })

    if (response.status === 'error') {
      showMessage({ type: 'error', message: response.error })
      return
    }
    showMessage({
      type: 'success',
      message: 'Account disconnected successfully.',
    })

    queryClient.invalidateQueries(['profile-integrations', companyId])
    queryClient.invalidateQueries(['content-refresh-domains', companyId])
    onDisconnect?.()
  }

  const handleClose = () => {
    setIsVisible(false)
    localStorage.removeItem('domain_property_selector')
  }

  useEffect(() => {
    const showPropertySelectorModal = localStorage.getItem(
      'domain_property_selector'
    )

    if (showPropertySelectorModal && domains.length > 0 && googleAccountId) {
      const hasAnyDisconnectedProperty = domains.some(
        d => !d.gsc_domain_property || !d.ga_property
      )
      if (hasAnyDisconnectedProperty) {
        setIsVisible(true)
      }
    }
  }, [domains.length, googleAccountId])

  if (gaError) {
    showMessage({ type: 'error', message: gaError })
  }

  if (gscError) {
    showMessage({ type: 'error', message: gscError })
  }

  return (
    <Modal
      visible={isVisible}
      width={640}
      onCancel={handleClose}
      title={
        <div className={s.header}>
          The Google account you connected contains access to some of the
          Domains you have already added
        </div>
      }
      okText="Save"
      onOk={handleSave}
      okButtonProps={{
        disabled: Object.keys(updatedValues).length === 0 || isLoading,
      }}
      destroyOnClose={true}
      className={s.modal}
    >
      <Spin spinning={isLoading}>
        <div className={s.wrapper}>
          <div className={s.connectedAccount}>
            <div className={s.account}>
              <GoogleLogo className={s.logo} />
              {email}
            </div>
            <PopOver
              visible={showDisconnectConfirmation}
              trigger="click"
              content={
                <div className={s.disconnectPopover}>
                  <DisconnectConfirmation
                    onDisconnect={handleDisconnect}
                    connectedDomains={domains}
                    onCancel={handleToggle}
                  />
                </div>
              }
              placement="leftTop"
            >
              <Button
                danger={true}
                type="link"
                size="middle"
                onClick={handleToggle}
              >
                Try Another Account
              </Button>
            </PopOver>
          </div>
          <div className={s.description}>
            Listed below are your domains that can be connected with your
            recently connected Google account.
          </div>
          <div className={s.domains}>
            {domains.map(domain => (
              <Domain
                domain={domain}
                key={domain.id}
                googleProperties={{ gaOptions, gscOptions }}
                companyId={companyId}
                onChange={handleChange}
              />
            ))}
          </div>
        </div>
      </Spin>
    </Modal>
  )
}
